import { ref } from 'vue'
import { defineStore } from 'pinia'
import { useUserStore } from '@/stores/user'
import { Products, getLabel } from '@/helpers/product'
import { ThriveCartProduct } from '@/helpers/thriveCartProduct'
import { useModalStore } from '@/stores/modal'
import { track } from '@/helpers/mixpanelDes'
import type { TAllProductsCart, TPlanConfig, TUpgradeSource } from '@/types/upgrade'
import type { TUser } from '@/types/user'

export const useUpgradeStore = defineStore('upgrade', () => {

  const user = useUserStore()
  const idStandard: number[] = user.getIdStandard()
  const idPro: number[] = user.getIdPro()
  const idPremium: number[] = user.getIdPremium()
  const idAgencyPremium: number[] = user.getIdAgencyPremium()
  const accountTypeId: number | undefined = user?.user?.account_type
  const allProductsCartUrl: TAllProductsCart = getAllProductsCartUrl(user.user)
  const modal = useModalStore()

  const config: TPlanConfig[] = [
    {
      name: getLabel(Products.Standard),
      price: 27,
      accountTypeIds: idStandard,
      currentPlan: idStandard.includes(accountTypeId),
      purchaseUrl: '',
      featuresLabel: 'Features:',
      features: [
        '10 Wordgenie v4 book generations/month',
        'Standard Templates',
        'Unlimited PDF eBooks',
        'Page Numbering & Table Of Contents Generator',
      ],
    },
    {
      name: getLabel(Products.Pro),
      price: 97,
      accountTypeIds: idPro,
      currentPlan: idPro.includes(accountTypeId),
      purchaseUrl: allProductsCartUrl.proUrl,
      featuresLabel: 'Everything in Standard, plus:',
      features: [
        '15 Wordgenie v4 book generations/month',
        'Pro Templates',
        'Publish to Kindle',
        '3D Cover Creator',
      ],
    },
    {
      name: getLabel(Products.Premium),
      price: 297,
      accountTypeIds: idPremium,
      currentPlan: idPremium.includes(accountTypeId),
      purchaseUrl: allProductsCartUrl.premiumUrl,
      featuresLabel: 'Everything in Pro, plus:',
      features: [
        'Unlimited Wordgenie v4 Manuscript Generations',
        'Create Presentations',
        'Publish Print Books',
        'Transcribe Videos and Audio',
        'Create Audiobooks',
      ],
    },
    {
      name: getLabel(Products.AgencyPremium),
      price: 497,
      accountTypeIds: idAgencyPremium,
      currentPlan: idAgencyPremium.includes(accountTypeId),
      purchaseUrl: allProductsCartUrl.agencypremiumUrl,
      featuresLabel: 'Everything in Premium, plus:',
      features: [
        'Custom Template Creator',
        'Collaborative eBooks with Client Interface',
        'Accounts for Agency Members',
      ],
    }
  ]

  function getAllProductsCartUrl (user: TUser): TAllProductsCart {
    return {
      proUrl: ThriveCartProduct.proProduct.getProductCartUrl(user),
      premiumUrl: ThriveCartProduct.premiumProduct.getProductCartUrl(user),
      agencypremiumUrl: ThriveCartProduct.agencyPremiumProduct.getProductCartUrl(user),
    }
  }

  const upgradeSource = ref<TUpgradeSource | null>(null)
  const requiredPlanId = ref<number | null>(null)

  function showUpgradeModal (source: TUpgradeSource, requiredPlan?: number) {
    upgradeSource.value = source
    requiredPlanId.value = requiredPlan ?? null
    track('upgrade-modal-opened', { source, required_plan: requiredPlan ?? null })

    modal.open(() => import('@/components/modal/DesModalUpgrade.vue'))
  }

  return { config, upgradeSource, requiredPlanId, getAllProductsCartUrl, showUpgradeModal }
})
